import { useState, useEffect } from "react";
import { X, Command, Search, Moon, Plus, HelpCircle, Layers } from "lucide-react";
import { cn } from "@/lib/utils";

const SHORTCUTS = [
  { keys: ["Ctrl", "N"], action: "New chat", icon: Plus },
  { keys: ["Ctrl", "K"], action: "Search conversations", icon: Search },
  { keys: ["Ctrl", "D"], action: "Toggle dark mode", icon: Moon },
  { keys: ["Ctrl", "B"], action: "Toggle bulk mode", icon: Layers },
  { keys: ["Ctrl", "/"], action: "Show this cheatsheet", icon: HelpCircle },
];

export function ShortcutCheatsheet() {
  const [open, setOpen] = useState(false);
  const isMac = typeof navigator !== "undefined" && navigator.platform.toUpperCase().includes("MAC");

  useEffect(() => {
    const handleKey = (e) => {
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (mod && e.key === "/") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, isMac]);

  return (
    <>
      {/* Floating trigger — desktop only */}
      <button
        data-testid="shortcut-cheatsheet-btn"
        onClick={() => setOpen(true)}
        className={cn(
          "hidden md:flex fixed bottom-6 right-6 z-40",
          "w-10 h-10 items-center justify-center rounded-full",
          "bg-card border border-border/60 shadow-lg",
          "text-muted-foreground hover:text-primary hover:border-primary/40",
          "transition-all duration-200 hover:scale-105 active:scale-95"
        )}
        title="Keyboard shortcuts (Ctrl+/)"
        aria-label="Keyboard shortcuts"
      >
        <span className="text-sm font-semibold">?</span>
      </button>

      {/* Modal overlay */}
      {open && (
        <div
          data-testid="shortcut-cheatsheet-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-card border border-border/50 rounded-2xl shadow-2xl w-full max-w-sm mx-4 animate-in zoom-in-95 slide-in-from-bottom-2 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div className="flex items-center gap-2">
                <Command className="w-4 h-4 text-primary" strokeWidth={2} />
                <h3 className="text-base font-semibold">Keyboard Shortcuts</h3>
              </div>
              <button
                data-testid="shortcut-cheatsheet-close"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Shortcut list */}
            <div className="px-5 pb-5 space-y-1">
              {SHORTCUTS.map(({ keys, action, icon: Icon }) => (
                <div
                  key={action}
                  className="flex items-center justify-between py-2.5 px-3 rounded-xl hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-center gap-2.5">
                    <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                    <span className="text-sm">{action}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {keys.map((k, i) => (
                      <kbd
                        key={i}
                        className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 text-xs font-mono font-medium bg-muted/60 border border-border/50 rounded-lg text-muted-foreground"
                      >
                        {k === "Ctrl" && isMac ? "⌘" : k}
                      </kbd>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer hint */}
            <div className="border-t border-border/30 px-5 py-3">
              <p className="text-[11px] text-muted-foreground text-center">
                Press <kbd className="px-1 py-0.5 text-[10px] font-mono bg-muted/50 border border-border/40 rounded">Esc</kbd> to close
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
